import jsPDF from "jspdf";

/**
 * PDF generation utility functions for consistent PDF export across the application
 */

export interface PDFDocument {
  doc: jsPDF;
  pageWidth: number;
  pageHeight: number;
  yPosition: number;
  margin: number;
  lineHeight: number;
  sectionSpacing: number;
}

/**
 * Initialize a new PDF document with standard settings
 */
export function initPDFDocument(title?: string): PDFDocument {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const lineHeight = 7;
  const sectionSpacing = 15;
  let yPosition = 20;

  // Add title if provided
  if (title) {
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor("#1B5E20");
    doc.text(title, margin, yPosition);
    yPosition += 15;
  }

  return {
    doc,
    pageWidth,
    pageHeight,
    yPosition,
    margin,
    lineHeight,
    sectionSpacing,
  };
}

/**
 * Add text with word wrap and auto-pagination
 */
export function addText(
  pdf: PDFDocument,
  text: string,
  fontSize: number = 10,
  isBold: boolean = false,
  color: string = "#000000",
  alignment: "left" | "center" | "right" = "left"
): void {
  pdf.doc.setFontSize(fontSize);
  pdf.doc.setFont("helvetica", isBold ? "bold" : "normal");
  pdf.doc.setTextColor(color);

  const maxWidth = alignment === "left" || alignment === "right"
    ? pdf.pageWidth - 2 * pdf.margin
    : pdf.pageWidth - 2 * pdf.margin;
  const lines = pdf.doc.splitTextToSize(text, maxWidth);

  lines.forEach((line: string) => {
    // Check page break before each line
    if (pdf.yPosition > pdf.pageHeight - 30) {
      pdf.doc.addPage();
      pdf.yPosition = pdf.margin;
    }

    const xPosition =
      alignment === "center"
        ? pdf.pageWidth / 2
        : alignment === "right"
          ? pdf.pageWidth - pdf.margin
          : pdf.margin;

    pdf.doc.text(line, xPosition, pdf.yPosition, {
      align: alignment,
    });
    pdf.yPosition += pdf.lineHeight;
  });
}

/**
 * Add a section header with improved styling
 */
export function addSectionHeader(
  pdf: PDFDocument,
  title: string,
  color: string = "#2E7D32"
): void {
  pdf.yPosition += pdf.sectionSpacing;
  addText(pdf, title, 14, true, color);
  pdf.yPosition += 3;

  // Add underline
  pdf.doc.setDrawColor(color);
  pdf.doc.setLineWidth(0.5);
  pdf.doc.line(pdf.margin, pdf.yPosition, pdf.pageWidth - pdf.margin, pdf.yPosition);
  pdf.yPosition += 8;
}

/**
 * Add a key-value pair with improved formatting
 */
export function addKeyValue(
  pdf: PDFDocument,
  key: string,
  value: string,
  keyColor: string = "#555555",
  valueColor: string = "#333333"
): void {
  const keyText = `${key}:`;
  const valueText = value || "N/A";

  // Check page break before starting
  checkPageBreak(pdf, 15);

  pdf.doc.setFontSize(10);
  pdf.doc.setFont("helvetica", "bold");
  pdf.doc.setTextColor(keyColor);

  const keyX = pdf.margin;
  pdf.doc.text(keyText, keyX, pdf.yPosition);

  pdf.doc.setFont("helvetica", "normal");
  pdf.doc.setTextColor(valueColor);

  const valueX = pdf.margin + 70; // Fixed offset for alignment
  const maxValueWidth = pdf.pageWidth - pdf.margin - valueX;
  const valueLines = pdf.doc.splitTextToSize(valueText, maxValueWidth);

  // Handle first line of value on same line as key
  if (valueLines.length > 0) {
    checkPageBreak(pdf, pdf.lineHeight);
    pdf.doc.text(valueLines[0], valueX, pdf.yPosition);

    // Handle wrapped lines on subsequent lines
    let wrapY = pdf.yPosition;
    for (let i = 1; i < valueLines.length; i++) {
      wrapY += pdf.lineHeight;

      // Check if we need a new page before adding wrapped line
      if (wrapY > pdf.pageHeight - 30) {
        pdf.doc.addPage();
        wrapY = pdf.margin;
        // Update pdf.yPosition to reflect page change
        pdf.yPosition = wrapY;
      }

      pdf.doc.text(valueLines[i], pdf.margin, wrapY);
    }

    // Update yPosition to the last line written
    pdf.yPosition = wrapY;
  }

  // Move to next line after this key-value pair
  pdf.yPosition += pdf.lineHeight;
}

/**
 * Add a horizontal divider line
 */
export function addDivider(pdf: PDFDocument, color: string = "#E0E0E0"): void {
  pdf.yPosition += 5;
  pdf.doc.setDrawColor(color);
  pdf.doc.setLineWidth(0.3);
  pdf.doc.line(pdf.margin, pdf.yPosition, pdf.pageWidth - pdf.margin, pdf.yPosition);
  pdf.yPosition += 10;
}

/**
 * Add a badge/chip-like indicator
 */
export function addBadge(
  pdf: PDFDocument,
  text: string,
  backgroundColor: string = "#E8F5E9",
  textColor: string = "#2E7D32"
): void {
  const textWidth = pdf.doc.getTextWidth(text);
  const padding = 4;
  const badgeWidth = textWidth + padding * 2;
  const badgeHeight = 6;

  // Draw background
  pdf.doc.setFillColor(backgroundColor);
  pdf.doc.roundedRect(pdf.margin, pdf.yPosition - badgeHeight, badgeWidth, badgeHeight, 2, 2, "F");

  // Add text
  pdf.doc.setFontSize(8);
  pdf.doc.setFont("helvetica", "bold");
  pdf.doc.setTextColor(textColor);
  pdf.doc.text(text, pdf.margin + padding, pdf.yPosition - 1);

  pdf.yPosition += badgeHeight + 3;
}

/**
 * Add footer with generation info
 */
export function addFooter(pdf: PDFDocument, note?: string): void {
  const totalPages = pdf.doc.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    pdf.doc.setPage(i);

    pdf.doc.setFontSize(8);
    pdf.doc.setFont("helvetica", "normal");
    pdf.doc.setTextColor("#666666");

    const footerY = pdf.pageHeight - 15;
    const generatedText = `Generated on: ${new Date().toLocaleString()}`;
    pdf.doc.text(generatedText, pdf.pageWidth / 2, footerY, { align: "center" });

    if (note) {
      pdf.doc.text(note, pdf.pageWidth / 2, footerY + 5, { align: "center" });
    }
  }
}

/**
 * Save PDF with filename
 */
export function savePDF(pdf: PDFDocument, fileName: string): void {
  pdf.doc.save(fileName);
}

/**
 * Check and add new page if needed
 */
export function checkPageBreak(pdf: PDFDocument, requiredSpace: number = 20): void {
  if (pdf.yPosition + requiredSpace > pdf.pageHeight - 30) {
    pdf.doc.addPage();
    pdf.yPosition = pdf.margin;
  }
}

